import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Sparkles, Plus, Loader2, Clock, CheckCircle2, XCircle, LogOut,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-172 — AI Creator dashboard.
 * Shows creator status (pending/approved/rejected), their avatars, and
 * the avatar create form (only enabled once creator is approved).
 */
const CreatorDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    first_name: '', avatar_url: '', style: 'realistic',
    languages: '', industries: '', content_formats: '',
    base_package_price: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/ai-creators/me');
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load creator profile');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.first_name) { toast.error('Avatar name required'); return; }
    setCreating(true);
    try {
      const csv = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
      await api.post('/ai-creators/me/avatars', {
        first_name: form.first_name,
        avatar_url: form.avatar_url || null,
        style: form.style,
        languages: csv(form.languages),
        industries: csv(form.industries),
        content_formats: csv(form.content_formats),
        base_package_price: form.base_package_price ? parseFloat(form.base_package_price) : null,
      });
      toast.success('Avatar submitted for review');
      setShowCreate(false);
      setForm({ first_name: '', avatar_url: '', style: 'realistic', languages: '', industries: '', content_formats: '', base_package_price: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally { setCreating(false); }
  };

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" /></div>;
  }
  if (!data) return null;

  const status = data.status;
  const tone =
    status === 'approved' ? { bg: 'bg-emerald-50',  border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2 } :
    status === 'rejected' ? { bg: 'bg-rose-50',     border: 'border-rose-200',    text: 'text-rose-700',    icon: XCircle } :
                            { bg: 'bg-amber-50',    border: 'border-amber-200',   text: 'text-amber-700',   icon: Clock };
  const Icon = tone.icon;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="creator-dashboard">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-fuchsia-500" /> Creator Studio
          </h1>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <Card className={`border ${tone.border} ${tone.bg}`} data-testid="creator-status-card">
          <CardContent className="p-5 flex items-center gap-4">
            <Icon className={`w-8 h-8 ${tone.text} shrink-0`} />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-gray-500">Application status</p>
              <p className={`text-lg font-semibold ${tone.text}`} data-testid="creator-status">{status}</p>
              {status === 'pending' && <p className="text-sm text-gray-600 mt-1">An admin will review your application shortly. You can submit avatars once approved.</p>}
              {status === 'rejected' && data.rejected_reason && <p className="text-sm text-rose-700 mt-1">Reason: {data.rejected_reason}</p>}
              {status === 'approved' && <p className="text-sm text-gray-600 mt-1">Welcome aboard. Submit your avatars and they'll go live once each one is approved.</p>}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your Avatars</h2>
          <Button
            onClick={() => setShowCreate(true)}
            disabled={status !== 'approved'}
            className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white disabled:opacity-40"
            data-testid="add-avatar-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Avatar
          </Button>
        </div>

        {(data.avatars || []).length === 0 ? (
          <Card><CardContent className="py-10 text-center text-gray-500">No avatars yet.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creator-avatars">
            {data.avatars.map(a => (
              <Card key={a.user_id} className="overflow-hidden">
                <div className="aspect-square bg-gradient-to-br from-violet-100 to-fuchsia-100">
                  {a.avatar_url
                    ? <img src={a.avatar_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-10 h-10 text-fuchsia-300" /></div>}
                </div>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="font-semibold truncate">{a.first_name} {a.last_name}</h3>
                    <Badge variant="outline" className={
                      a.avatar_status === 'approved' ? 'border-emerald-300 text-emerald-700 text-[10px]' :
                      a.avatar_status === 'rejected' ? 'border-rose-300 text-rose-700 text-[10px]' :
                      'border-amber-300 text-amber-700 text-[10px]'
                    }>{a.avatar_status}</Badge>
                  </div>
                  {a.avatar_status === 'rejected' && a.rejected_reason && (
                    <p className="text-[11px] text-rose-600 mt-1">{a.rejected_reason}</p>
                  )}
                  <p className="text-[11px] text-gray-500 mt-1">
                    {a.style || 'unspecified'} · {a.base_package_price ? `from $${a.base_package_price}` : 'pricing TBD'}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-avatar-dialog">
          <DialogHeader><DialogTitle>Submit new avatar</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Avatar name *</Label><Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} data-testid="avatar-name-input" /></div>
            <div><Label>Avatar image URL</Label><Input value={form.avatar_url} onChange={e => setForm(f => ({...f, avatar_url: e.target.value}))} placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Style</Label>
                <select className="w-full h-9 border border-gray-200 rounded text-sm px-2" value={form.style} onChange={e => setForm(f => ({...f, style: e.target.value}))}>
                  <option value="realistic">Realistic</option>
                  <option value="stylized">Stylized</option>
                  <option value="animated">Animated</option>
                </select>
              </div>
              <div><Label>Base package $</Label><Input type="number" value={form.base_package_price} onChange={e => setForm(f => ({...f, base_package_price: e.target.value}))} /></div>
            </div>
            <div><Label>Languages (comma-separated)</Label><Input value={form.languages} onChange={e => setForm(f => ({...f, languages: e.target.value}))} placeholder="en, es" /></div>
            <div><Label>Industries</Label><Input value={form.industries} onChange={e => setForm(f => ({...f, industries: e.target.value}))} placeholder="beauty, tech" /></div>
            <div><Label>Content formats</Label><Input value={form.content_formats} onChange={e => setForm(f => ({...f, content_formats: e.target.value}))} placeholder="ugc, spokesperson" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
            <Button onClick={submit} disabled={creating} className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white" data-testid="submit-avatar-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit for review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatorDashboard;
